/**
 * Logger Utility für strukturiertes, environment-basiertes Logging
 *
 * Usage:
 *   import logger from '../utility/logger';
 *   logger.websocket.info('Connection opened');
 *   logger.websocket.error('Connection failed', error);
 */

// Log Levels
export enum LogLevel {
  ERROR = 0,
  WARN = 1,
  INFO = 2,
  DEBUG = 3,
}

// Logger Kategorien
export enum LogCategory {
  WEBSOCKET = 'WebSocket',
  API = 'API',
  CONTEXT = 'Context',
  FORM = 'Form',
  GENERAL = 'General',
}

class Logger {
  private isDevelopment: boolean;
  private logLevel: LogLevel;

  constructor() {
    this.isDevelopment = process.env.NODE_ENV === 'development';
    // Log-Level aus Environment-Variable oder Default auf INFO
    this.logLevel =
      this.parseLogLevel(process.env.REACT_APP_LOG_LEVEL) || LogLevel.INFO;
  }

  private parseLogLevel(level?: string): LogLevel {
    switch (level?.toUpperCase()) {
      case 'ERROR':
        return LogLevel.ERROR;
      case 'WARN':
        return LogLevel.WARN;
      case 'INFO':
        return LogLevel.INFO;
      case 'DEBUG':
        return LogLevel.DEBUG;
      default:
        return LogLevel.INFO;
    }
  }

  private shouldLog(level: LogLevel): boolean {
    // In Production: Nur Errors loggen (außer explizit anders konfiguriert)
    if (!this.isDevelopment && level > LogLevel.ERROR) {
      return false;
    }
    return level <= this.logLevel;
  }

  private formatMessage(category: LogCategory, message: string): string {
    const timestamp = new Date().toISOString();
    return `[${timestamp}] [${category}] ${message}`;
  }

  private log(
    level: LogLevel,
    category: LogCategory,
    message: string,
    ...args: any[]
  ): void {
    if (!this.shouldLog(level)) return;

    const formattedMessage = this.formatMessage(category, message);

    switch (level) {
      case LogLevel.ERROR:
        console.error(formattedMessage, ...args);
        break;
      case LogLevel.WARN:
        console.warn(formattedMessage, ...args);
        break;
      case LogLevel.INFO:
        console.info(formattedMessage, ...args);
        break;
      case LogLevel.DEBUG:
        console.debug(formattedMessage, ...args);
        break;
    }
  }

  // Category-spezifische Logger
  public websocket = {
    error: (message: string, ...args: any[]) =>
      this.log(LogLevel.ERROR, LogCategory.WEBSOCKET, message, ...args),
    warn: (message: string, ...args: any[]) =>
      this.log(LogLevel.WARN, LogCategory.WEBSOCKET, message, ...args),
    info: (message: string, ...args: any[]) =>
      this.log(LogLevel.INFO, LogCategory.WEBSOCKET, message, ...args),
    debug: (message: string, ...args: any[]) =>
      this.log(LogLevel.DEBUG, LogCategory.WEBSOCKET, message, ...args),
  };

  public api = {
    error: (message: string, ...args: any[]) =>
      this.log(LogLevel.ERROR, LogCategory.API, message, ...args),
    warn: (message: string, ...args: any[]) =>
      this.log(LogLevel.WARN, LogCategory.API, message, ...args),
    info: (message: string, ...args: any[]) =>
      this.log(LogLevel.INFO, LogCategory.API, message, ...args),
    debug: (message: string, ...args: any[]) =>
      this.log(LogLevel.DEBUG, LogCategory.API, message, ...args),
  };

  public context = {
    error: (message: string, ...args: any[]) =>
      this.log(LogLevel.ERROR, LogCategory.CONTEXT, message, ...args),
    warn: (message: string, ...args: any[]) =>
      this.log(LogLevel.WARN, LogCategory.CONTEXT, message, ...args),
    info: (message: string, ...args: any[]) =>
      this.log(LogLevel.INFO, LogCategory.CONTEXT, message, ...args),
    debug: (message: string, ...args: any[]) =>
      this.log(LogLevel.DEBUG, LogCategory.CONTEXT, message, ...args),
  };

  public form = {
    error: (message: string, ...args: any[]) =>
      this.log(LogLevel.ERROR, LogCategory.FORM, message, ...args),
    warn: (message: string, ...args: any[]) =>
      this.log(LogLevel.WARN, LogCategory.FORM, message, ...args),
    info: (message: string, ...args: any[]) =>
      this.log(LogLevel.INFO, LogCategory.FORM, message, ...args),
    debug: (message: string, ...args: any[]) =>
      this.log(LogLevel.DEBUG, LogCategory.FORM, message, ...args),
  };

  public general = {
    error: (message: string, ...args: any[]) =>
      this.log(LogLevel.ERROR, LogCategory.GENERAL, message, ...args),
    warn: (message: string, ...args: any[]) =>
      this.log(LogLevel.WARN, LogCategory.GENERAL, message, ...args),
    info: (message: string, ...args: any[]) =>
      this.log(LogLevel.INFO, LogCategory.GENERAL, message, ...args),
    debug: (message: string, ...args: any[]) =>
      this.log(LogLevel.DEBUG, LogCategory.GENERAL, message, ...args),
  };
}

// Singleton-Instanz exportieren
const logger = new Logger();
export default logger;
